import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faSearch,
  faPen,
  faEye,
  faPlus,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faTrash,
  faMagnifyingGlass,
  faChevronUp,
  faChevronDown,
  faToggleOn,
  faToggleOff,
} from '@fortawesome/pro-solid-svg-icons';
import { faHandshake } from '@fortawesome/pro-light-svg-icons';
import { HostListener } from '@angular/core';
import { PartnerService, ListPartnersParams } from '../../../../core/services/partner.service';
import { Partner, PARTNER_TYPE_OPTIONS } from '../../../../core/models/partner.model';
import { USER_TYPE_OPTIONS } from '../../../../core/models/user.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EditPartnerModal } from '../components/edit-partner-modal/edit-partner-modal';
import { PartnerFilesModalComponent } from '../components/partner-files-modal/partner-files-modal';
import { faFile } from '@fortawesome/pro-solid-svg-icons';
import { PopupInfoService } from '../../../../shared/services/popup-info.service';
import { PopupConfirmService } from '../../../../shared/services/popup-confirm.service';
import { CustomSelectComponent } from '../../../../shared/components/custom-select/custom-select.component';

@Component({
  selector: 'app-partners-list-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    FontAwesomeModule,
    EditPartnerModal,
    PartnerFilesModalComponent,
    CustomSelectComponent,
  ],
  templateUrl: './partners-list.html',
  styleUrls: ['./partners-list.scss'],
})
export class PartnersListPage implements OnInit {
  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);
  private readonly popupConfirmService = inject(PopupConfirmService);

  faSearch = faSearch;
  faHandshake = faHandshake;
  faPen = faPen;
  faEye = faEye;
  faPlus = faPlus;
  faFilter = faFilter;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faEllipsisVertical = faEllipsisVertical;
  faTrash = faTrash;
  faMagnifyingGlass = faMagnifyingGlass;
  faFile = faFile;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;

  partners: Partner[] = [];
  totalPartners = 0;
  searchQuery = '';
  isLoading = false;

  typeOptions = PARTNER_TYPE_OPTIONS;
  relationshipTypeOptions = USER_TYPE_OPTIONS.filter((option) => option.value !== 'Administrador');
  statusOptions: { label: string; value: Partner['status'] }[] = [
    { label: 'Activo', value: 'active' },
    { label: 'Inactivo', value: 'inactive' },
  ];

  typeFilter = '';
  relationshipTypeFilter = '';
  statusFilter = '';
  isLoadingMore = false;
  currentPage = 1;
  perPage = 30;
  lastPage = 1;

  sortField: string = 'company_name';
  sortOrder: 'asc' | 'desc' = 'asc';
  openMenuId: string | null = null;
  menuPosition: { top: number; left: number } | null = null;
  private readonly menuWidth = 192; // w-48
  private readonly menuHeight = 140; // altura aproximada del menú (3 opciones)



  selectedPartnerForEdit: Partner | null = null;
  selectedPartnerForFiles: Partner | null = null;
  updatingStatusId: string | null = null;

  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    this.loadPartners();

    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe((term) => {
      this.searchQuery = term;
      this.currentPage = 1;
      this.loadPartners();
    });
  }

  loadPartners(append = false): void {
    if (this.isLoading || (append && this.currentPage >= this.lastPage)) return;

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.currentPage = 1;
    }

    const params: any = {
      page: append ? this.currentPage + 1 : 1,
      per_page: this.perPage,
      search: this.searchQuery,
      sort_by: this.sortField,
      sort_direction: this.sortOrder,
    };

    if (this.typeFilter) params.type = this.typeFilter;
    if (this.relationshipTypeFilter) params.relationship_type = this.relationshipTypeFilter;
    if (this.statusFilter) params.status = this.statusFilter;

    this.partnerService.getPartners(params).subscribe({
      next: (response) => {
        if (append) {
          this.partners = [...this.partners, ...response.data];
        } else {
          this.partners = response.data;
        }
        this.currentPage = response.meta.current_page;
        this.totalPartners = response.meta.total;
        this.lastPage = response.meta.last_page;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Error loading partners:', err);
        this.isLoading = false;
        this.isLoadingMore = false;
      },
    });
  }

  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadPartners();
  }

  clearFilters(): void {
    this.typeFilter = '';
    this.relationshipTypeFilter = '';
    this.statusFilter = '';
    this.onFilterChange();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // El menú de acciones está posicionado en fixed respecto al botón que lo abrió;
    // al hacer scroll dejaría de estar alineado, así que lo cerramos.
    this.closeMenu();

    if (this.isLoading || this.isLoadingMore || this.currentPage >= this.lastPage) return;

    const pos =
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) +
      window.innerHeight;
    const max = document.documentElement.scrollHeight;

    // Si estamos a menos de 400px del final de la página, cargamos más
    if (pos >= max - 400) {
      this.loadPartners(true);
    }
  }

  openEditModal(partner: Partner, event: Event): void {
    event.stopPropagation();
    this.selectedPartnerForEdit = { ...partner };
  }

  closeEditModal(): void {
    this.selectedPartnerForEdit = null;
  }

  openFilesModal(partner: Partner, event: Event): void {
    event.stopPropagation();
    this.selectedPartnerForFiles = { ...partner };
  }

  closeFilesModal(): void {
    this.selectedPartnerForFiles = null;
  }

  toggleStatus(partner: Partner, event: Event): void {
    event.stopPropagation();
    this.closeMenu();

    const activating = partner.status !== 'active';
    const newStatus: Partner['status'] = activating ? 'active' : 'inactive';

    this.popupConfirmService.showConfirm({
      title: activating ? '¿Activar partner?' : '¿Desactivar partner?',
      message: activating
        ? `¿Estás seguro de que deseas activar a "${partner.company_name}"?`
        : `¿Estás seguro de que deseas desactivar a "${partner.company_name}"?`,
      confirmText: activating ? 'Activar' : 'Desactivar',
      cancelText: 'Cancelar',
      type: activating ? 'info' : 'danger',
      onConfirm: () => {
        this.updatingStatusId = partner.id;
        this.partnerService.updateStatus(partner.id, newStatus).subscribe({
          next: (response) => {
            partner.status = response.data.status;
            this.updatingStatusId = null;
            this.popupInfoService.showSuccess(
              `Partner ${activating ? 'activado' : 'desactivado'} correctamente`
            );
          },
          error: () => {
            this.updatingStatusId = null;
            this.popupInfoService.showError('Error al actualizar el estado del partner');
          },
        });
      },
    });
  }

  onEditSuccess(updatedPartner: Partner): void {
    const index = this.partners.findIndex((p) => p.id === updatedPartner.id);
    if (index !== -1) {
      this.partners[index] = updatedPartner;
    }
    this.closeEditModal();
  }

  onSort(column: string): void {
    if (this.sortField === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = column;
      this.sortOrder = 'asc';
    }
    this.currentPage = 1;
    this.loadPartners();
  }

  getSortIcon(column: string) {
    if (this.sortField !== column) return this.faSort;
    return this.sortOrder === 'asc' ? this.faSortUp : this.faSortDown;
  }

  toggleMenu(id: string, event: Event): void {
    event.stopPropagation();

    if (this.openMenuId === id) {
      this.closeMenu();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const opensUpward = rect.bottom + 8 + this.menuHeight > window.innerHeight;

    this.menuPosition = {
      top: opensUpward ? Math.max(8, rect.top - 8 - this.menuHeight) : rect.bottom + 8,
      left: Math.max(8, rect.right - this.menuWidth),
    };
    this.openMenuId = id;
  }

  closeMenu(): void {
    this.openMenuId = null;
    this.menuPosition = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }

  getContractStatus(partner: Partner): 'pending' | 'signed' | null {
    return partner.contract?.status ?? partner.contract_status ?? null;
  }

  isPartnerInactive(partner: Partner): boolean {
    return partner.status !== 'active';
  }

  partnerStatusLabel(partner: Partner): string {
    return partner.status === 'archived' ? 'Archivado' : 'Inactivo';
  }

  formatPercent(value: number | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return `${value}%`;
  }

  formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }
}
